import { ChangeDetectorRef, Component, ElementRef, Inject, LOCALE_ID, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DecimalPipe } from '@angular/common';
import { AuthService } from 'src/app/shared';
import {
    CalculationType, LocationCalculationDetail, LocationDetailView,
    LocationOrigin, QualityCriterionType, RiskAssessmentStatus
} from 'src/app/contracts';
import { CalculationService, LocationService } from 'src/app/services';
import { EMPTY, Subject } from 'rxjs';
import { catchError, filter, finalize, switchMap, tap } from 'rxjs/operators';

@Component({
    selector: 'app-preliminary-location-details',
    templateUrl: './preliminary-location-details.component.html',
    styleUrls: ['./preliminary-location-details.component.scss']
})
export class PreliminaryLocationDetailsComponent implements OnInit, OnDestroy {
    @ViewChild('addCommentModalButton') private readonly addCommentModalButton: ElementRef;
    @ViewChild('deleteCalculationModalButton') private readonly deleteCalculationModalButton: ElementRef;

    public location: LocationDetailView = new LocationDetailView({});
    public get publicActionPerformed(): boolean {
        return !!this.location && this.location.origin !== LocationOrigin.V1asV2Calculation;
    }

    public locationOrigin = LocationOrigin;
    public riskAssessmentStatus = RiskAssessmentStatus;
    public qualityCriterionType = QualityCriterionType;

    // Current calculation when doing modal-stuff
    public modalCalculationId: string;
    public isExternalUser = false;
    public pageRequest = {
        type: '',
        payload: null
    };
    public pageActions = {
        loadModel: 'loadModel',
        addCalculation: 'addCalculation',
        addComment: 'addComment',
        deleteCalculation: 'deleteCalculation'
    };
    public pageStates = {
        isLoadingModel: false,
        isCaculating: false,
        isDeleting: false,
        isAddingComment: false
    };

    private readonly action$ = new Subject<any>();
    private readonly loadModel = this.action$.pipe(
        filter(request => request.type === this.pageActions.loadModel),
        switchMap(request => this.loadModelDetail(request.payload))
    ).subscribe();

    private readonly addComment = this.action$.pipe(
        filter(request => request.type === this.pageActions.addComment),
        switchMap(request =>
            this.addCalculationComment(request.payload).pipe(
                tap(_ => {
                    this.action$.next({ type: this.pageActions.loadModel, payload: this.location.id });
                })
            )
        )
    ).subscribe();

    constructor(
        @Inject(LOCALE_ID) private readonly locale,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly authService: AuthService,
        private readonly locationService: LocationService,
        private readonly calculationService: CalculationService,
        private readonly cdref: ChangeDetectorRef) {
    }

    public ngOnInit(): void {
        this.isExternalUser = this.authService.isExternalUser();

        const id = this.route.snapshot.paramMap.get('id');
        this.action$.next({ type: this.pageActions.loadModel, payload: id });
    }

    public ngOnDestroy(): void {
        this.action$.unsubscribe();
        this.loadModel.unsubscribe();
        this.addComment.unsubscribe();
    }

    public newCalculation(screeningCalculation: LocationCalculationDetail): void {
        if (!screeningCalculation) {
            return;
        }

        this.calculationService.createFromScreening(this.location.id,
            this.location.name,
            this.location.number,
            screeningCalculation.compoundCasNr,
            screeningCalculation.compoundName,
            screeningCalculation.screeningData,
            CalculationType.PreliminaryCalculation);

        this.router.navigate(['riskassessment/select-model']);
    }

    public goToCalculationResults(id: string): void {
        this.router.navigate(['riskassessment/calculation-result/' + id]);
    }

    public showAddCommentModal(calculationId: string): void {
        this.modalCalculationId = calculationId;
        this.addCommentModalButton.nativeElement.click();
    }

    public addCommentFromModal(comment: string): void {
        this.action$.next({ type: this.pageActions.addComment, payload: comment });
    }

    public getValidNumber(val: number): number {
        const result = (!val || isNaN(val) || !Number.isFinite(val)) ? 0 : val;
        return result;
    }

    public getNumberWithSignificantDigits(val: number): string {
        const n = this.getValidNumber(val);
        let digitsInfo = '';
        if (n >= 10.0) { // xx.x
            digitsInfo = '1.1-1';
        }
        else if (n >= 1.0) { // x.xx
            digitsInfo = '1.2-2';
        }
        else if (n >= 0.1) { // 0.xxx
            digitsInfo = '1.3-3';
        }
        else if (n >= 0.01) { // 0.0xxx
            digitsInfo = '1.4-4';
        }
        else { // 0.00xxx
            digitsInfo = '1.5-5';
        }

        const result = new DecimalPipe(this.locale).transform(n, digitsInfo);
        return result;
    }

    public getEllipsisText(text: string, maxLength: number): string {
        if (text !== null) {
            return text.length > maxLength ? text.substr(0, maxLength) + '...' : text;
        }
        return '';
    }

    private loadModelDetail(id: string): any {
        this.pageStates.isLoadingModel = true;
        return this.locationService.getLocationDetail(id).pipe(
            tap(res => {
                res.preliminaryCalculations = res.preliminaryCalculations
                    .sort((current, next) => new Date(current.createdAt) > new Date(next.createdAt) ? -1 : 1);
                res.preliminaryResults = res.preliminaryResults
                    .sort((current, next) => current.excessFactor > next.excessFactor ? -1 : 1);

                this.location = res;

                if (this.isExternalUser) {
                    this.location.origin = LocationOrigin.PublicAction;
                }
            }),
            catchError(() => {
                this.pageStates.isLoadingModel = false;
                return EMPTY;
            }),
            finalize(() => {
                this.pageStates.isLoadingModel = false;
                this.cdref.markForCheck();
            })
        );
    }

    private addCalculationComment(comment: string): any {
        this.pageStates.isAddingComment = true;
        const calcId = this.modalCalculationId;
        return this.calculationService.addCommentToCalculation(calcId, comment).pipe(
            catchError(() => {
                this.pageStates.isAddingComment = false;
                return EMPTY;
            }),
            finalize(() => {
                // We need to wait for the location detail to reloading completed
                setTimeout(() => {
                    this.modalCalculationId = '';
                    this.pageStates.isAddingComment = false;
                    this.cdref.markForCheck();
                }, 1000);
            })
        );
    }
}
